/**
 * Shared Cesium treatment for the Anomaly-Queue MAP surface — the AI-fused
 * High-Risk Hotspots, Flood, and Ice-Storm anomaly layers.
 *
 * Centralises the founder marker-doctrine revision (2026-06-05, §3B.8) so the
 * three layers stay in lockstep:
 *   - SIZE encodes Response Level; the dot is the SAME anomaly red at all levels
 *     (styles/responseLevel.ts → ANOMALY_MAP_RED).
 *   - MOTION BUDGET: only Level 3/4 pulse. Level 1/2 (+ unknown) are STATIC.
 *   - Level 1/2 are HIDDEN at country zoom and tier in at regional+; Level 3/4
 *     are visible at all zoom tiers.
 *   - Level 4 keeps its animated outer ring.
 *
 * The pure level→style mapping lives in styles/responseLevel.ts (unit-tested
 * without Cesium); this module is the thin Cesium adapter over it.
 */
import * as Cesium from 'cesium';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';
import {
  ICON_LADDER,
  ICON_LADDER_UNKNOWN_FLOOR,
  type HotspotLevelStyle,
  type ResponseLevel,
} from '../styles/responseLevel';

/** Pulse period (seconds) — matches the active-fire / live-camera breathe cadence. */
const PULSE_PERIOD_S = 2;

/* ───────────────────────── Alarm-lifecycle law (Colour Law 1/3) ────────────── *
 *  design-council-2026-06-05.md Part II + Part III Law 3. The lifecycle hue is
 *  GLOBAL across every anomaly family (family identity stays in the glyph):
 *
 *    new        → URGENT-RED #ff3b30 pulsing (styles/responseLevel ANOMALY_MAP_RED)
 *    acknowledged → ACK-STEEL #7d8da1 α0.75, STATIC, drops from country zoom
 *    cleared    → grey ghost #4b5563 α0.4 for ~60s, then auto-leave
 *
 *  Acknowledged + cleared are decoded from the persisted unified workstate
 *  (migration 0058 #140 — work_acknowledged_at), so they survive reload + handover
 *  and are the substrate #129's re-alarm clears on escalation.
 * ───────────────────────────────────────────────────────────────────────────── */

/** Reserved Law 1 token: acknowledged / downgraded anomaly marker. */
export const ACK_STEEL = '#7d8da1';
/** Acknowledged marker fill opacity (Law 3). */
export const ACK_STEEL_ALPHA = 0.75;
/** Acknowledged AREA (polygon) fill opacity (Law 3) — dimmer than the base ambient
 *  ≤0.18 so an acknowledged CAP field RECEDES rather than reads as a fresh warning. */
export const ACK_POLYGON_FILL_ALPHA = 0.08;
/** Acknowledged AREA (polygon) stroke opacity (Law 3) — receded outline. */
export const ACK_POLYGON_STROKE_ALPHA = 0.4;
/** Reserved Law 3 token: a cleared anomaly's brief grey ghost. */
export const CLEARED_GHOST = '#4b5563';
/** Cleared-ghost fill opacity (Law 3). */
export const CLEARED_GHOST_ALPHA = 0.4;
/** Cleared-ghost lifetime before auto-leave (Law 3 — "brief grey ghost ~60s"). */
export const GHOST_TTL_MS = 60_000;

/** The lifecycle-relevant subset of an anomaly feature's properties. `status` is
 *  the flood/ice own-status column (which the workstate PUT also mirrors); the
 *  unified `work_acknowledged_at` covers all five sources. */
export interface AnomalyLifecycleProps {
  work_acknowledged_at?: string | null;
  status?: string | null;
}

/**
 * True when an anomaly has been acknowledged (Law 3): a persisted unified-workstate
 * ack (`work_acknowledged_at`) OR a flood/ice own-status `'acknowledged'` flip
 * (kept in lockstep by the workstate PUT). Tolerant of partial properties.
 */
export function isAcknowledged(props: AnomalyLifecycleProps | null | undefined): boolean {
  if (!props) return false;
  return props.work_acknowledged_at != null || props.status === 'acknowledged';
}

/**
 * Apply the Law 3 acknowledged override to a base anomaly level-style: recolour to
 * ACK-STEEL at α0.75, go STATIC (no pulse, no ring), and drop from country zoom
 * (tiers in at regional+). The base size is preserved so the level is still legible
 * regionally; the lifecycle hue is global, family identity stays in the glyph.
 */
export function acknowledgedStyle(base: HotspotLevelStyle): HotspotLevelStyle {
  return {
    ...base,
    color: ACK_STEEL,
    alpha: ACK_STEEL_ALPHA,
    pulse: false,
    pulseAmplitude: 0,
    ring: false,
    hideAtCountry: true,
    label: `${base.label} · acknowledged`,
  };
}

/**
 * Scale an anomaly marker's geometry by a constant factor, preserving colour,
 * lifecycle, pulse, ring, and zoom-gate semantics. Only `baseSize` and
 * `pulseAmplitude` scale (the size channel), so the breathe stays proportional.
 *
 * Used by the WALL render profile (#132, council Part V) to draw the Tier-0/1
 * reds at ~2× so they read from across a room; `scale === 1` is a no-op so the
 * operator COP is untouched. Honours the colour law (hue unchanged) and the
 * motion law (a static style stays static — pulse flag is preserved).
 */
export function scaleAnomalyStyle(style: HotspotLevelStyle, scale: number): HotspotLevelStyle {
  if (scale === 1) return style;
  return {
    ...style,
    baseSize: style.baseSize * scale,
    pulseAmplitude: style.pulseAmplitude * scale,
  };
}

/**
 * Resolve the effective lifecycle style for an anomaly marker: acknowledged →
 * ACK-STEEL static (drops from country zoom); otherwise the unchanged base level
 * style (urgent → URGENT-RED, pulse on L3/L4). The single chokepoint every dot
 * anomaly layer routes through so the lifecycle stays in lockstep.
 */
export function lifecycleStyle(
  base: HotspotLevelStyle,
  props: AnomalyLifecycleProps | null | undefined,
): HotspotLevelStyle {
  return isAcknowledged(props) ? acknowledgedStyle(base) : base;
}

/**
 * WALL-side lift of the Law-3 acked country-zoom drop (#272, design-council
 * PART XI Ruling 1.6 — wall-scope amendment to Law 3 / PART II.2): on the wall,
 * an acknowledged-but-still-live anomaly renders at EVERY zoom as a static
 * ACK-STEEL pin — the wall's job is the room's shared picture of WHAT IS BEING
 * WORKED, so it structurally cannot false-quiet because one desk acked
 * everything. Hue/alpha/static-ness are untouched (the salience knob at wall
 * distance is size/alpha within the wall profile, NEVER hue — Ruling 1.5);
 * only the zoom gate lifts, and ONLY for acked styles — fresh L1/L2 keep
 * their §3B.8 regional tier-in on the wall exactly as on the COP. The desk
 * COP behavior is untouched (`lift=false` is the identity).
 */
export function liftAckedCountryDrop(
  style: HotspotLevelStyle,
  props: AnomalyLifecycleProps | null | undefined,
  lift: boolean,
): HotspotLevelStyle {
  return lift && isAcknowledged(props) && style.hideAtCountry
    ? { ...style, hideAtCountry: false }
    : style;
}

/** The fill/stroke a CAP-style AREA anomaly needs to render — the polygon analogue
 *  of HotspotLevelStyle for dots. Structurally matches capHazards' ExtremeWeatherStyle. */
export interface AnomalyPolygonStyle {
  color: string;
  alpha: number;
  strokeAlpha: number;
}

/**
 * Resolve the effective lifecycle style for an anomaly AREA (polygon) — the polygon
 * sibling of lifecycleStyle for markers. Acknowledged → recolour to ACK-STEEL and
 * RECEDE (dim fill + stroke); otherwise the unchanged base hazard style. Callers
 * additionally drop acknowledged polygons from country zoom (anomalyGate semantics)
 * since a polygon has no HotspotLevelStyle.hideAtCountry flag to carry it. This is
 * the single Law 3 chokepoint CAP polygons route through.
 */
export function lifecyclePolygonStyle(
  base: AnomalyPolygonStyle,
  props: AnomalyLifecycleProps | null | undefined,
): AnomalyPolygonStyle {
  if (!isAcknowledged(props)) return base;
  return { color: ACK_STEEL, alpha: ACK_POLYGON_FILL_ALPHA, strokeAlpha: ACK_POLYGON_STROKE_ALPHA };
}

/**
 * Pure decision for the cleared-ghost (Law 3). Given the ids present in the
 * PREVIOUS render and the LIVE ids now, returns the ids that should spawn a ghost
 * — but ONLY when the view (bboxKey) is unchanged between the two renders. On a
 * pan/zoom (bbox changed) a departure is a view change, NOT a clear, so we ghost
 * nothing (avoids false ghosts littering the map on every camera move).
 */
export function departedGhostIds(
  prevIds: Iterable<string>,
  liveIds: Set<string>,
  prevBboxKey: string | null,
  bboxKey: string,
): string[] {
  if (prevBboxKey !== bboxKey) return [];
  const out: string[] = [];
  for (const id of prevIds) {
    if (!liveIds.has(id)) out.push(id);
  }
  return out;
}

/* ─────── L4 fill-breathe on the DISC billboard (icon rebuild Phase 2a, §C.1-3) ─────── *
 *  The EXACT-ACT disc marker (discGlyphComposite) is ONE billboard whose image bakes the
 *  Response-Level-filled disc + white knocked-out glyph. §C.1-3: the L4 pulse is a FILL-breathe
 *  at a FIXED size (no size-grow). billboard.color MULTIPLIES the baked image, so — unlike the
 *  point-based anomalyLevelFill above (which lerps the point FILL toward white) — it can only
 *  DARKEN, never brighten past the authored image. The breathe is therefore a dim↔authored
 *  throb: at the sine PEAK the multiplier is white (identity → the authored red disc + white
 *  glyph, the "loud" state); at the trough a uniform grey dims the whole marker and returns.
 *  UNIFORM grey (r=g=b) so the throb never hue-shifts the disc (colour law) nor tints the white
 *  glyph pink (14px legibility). Shares the 2s anomalyGlowPhase01 heartbeat with the other
 *  families' glow swell so a mixed COP beats in phase.
 *
 *  (Render-fork advisory 2026-07-05: multiply-dim BEATS an image-swap "brighten toward white"
 *  — lightening the disc past a ~0.15 lerp washes the white glyph below the 3:1 floor, so a
 *  brighten buys LESS usable amplitude than this dim does, and its loud state is semantically
 *  backwards (disc furthest from the level colour). [FLAG-HUMAN] eyeball: if the throb reads
 *  too quiet on the globe, the one-line flip is the transparent-knockout white-disc variant —
 *  bake the disc WHITE with the glyph a transparent HOLE so billboard.color IS the fill and the
 *  brighten-toward-white headroom returns — NOT pre-baked frame swaps.) */

/** Trough RGB multiplier DEPTH for the L4 disc breathe — trough grey = 1 − 0.35 = 0.65. Tuned
 *  so the dimmed white glyph stays at the ~3:1 graphics-legibility floor (transiently) and the
 *  throb reads as a breathe, not flicker (keep within [0.25, 0.40]). */
export const ANOMALY_L4_BB_DIM = 0.35;
/** Trough ALPHA dip for the L4 disc breathe — trough α = 1 − 0.10 = 0.90 (≤0.10 so the disc
 *  edge never dissolves into busy imagery at the trough). */
export const ANOMALY_L4_BB_ALPHA_DIP = 0.1;

/** 0→1 swell phase on the shared 2s pulse period — trough (0) → peak (1). The single
 *  heartbeat behind every ambient map pulse; the L4 disc fill-breathe rides it (icon
 *  one-swing #564: the glow-halo consumers retired, so this is the sole remaining user). */
function pulsePhase01(): number {
  const t = performance.now() / 1000;
  return 0.5 + 0.5 * Math.sin((t * 2 * Math.PI) / PULSE_PERIOD_S);
}

/**
 * The billboard.color for an EXACT-ACT disc marker under the §C.1-3 fill-breathe. `pulse` (only
 * the unacked L4 in this family) → an animated CallbackProperty: a uniform-grey dim↔white
 * multiply throb on the shared 2s heartbeat (peak = white = the authored red disc; trough =
 * 0.65 grey / α0.90). Otherwise Cesium.Color.WHITE at `alpha` — the identity-hue multiplier that
 * shows the baked disc image (level / steel fill) at the level's opacity (L1/L2 muted, L3 amber,
 * a worked marker's steel disc at ACK-STEEL α — the P1 alpha hierarchy, preserved on the disc).
 * The IMAGE carries the hue; this modulates only brightness + opacity — the size cue stays the
 * disc's own fixed width/height (no pixelSize animation, §C.1-3).
 */
export function anomalyLevelBillboardColor(
  pulse: boolean,
  alpha = 1,
): Cesium.Color | Cesium.CallbackProperty {
  if (!pulse) return Cesium.Color.WHITE.withAlpha(alpha);
  return new Cesium.CallbackProperty((_time: unknown, result?: Cesium.Color) => {
    const p = pulsePhase01(); // 0 (trough) → 1 (peak) on the shared PULSE_PERIOD_S heartbeat
    const k = 1 - ANOMALY_L4_BB_DIM * (1 - p); // uniform grey multiplier: 0.65 (trough) → 1 (peak)
    const out = result ?? new Cesium.Color();
    out.red = k;
    out.green = k;
    out.blue = k;
    out.alpha = 1 - ANOMALY_L4_BB_ALPHA_DIP * (1 - p); // 0.90 (trough) → 1 (peak)
    return out;
  }, false);
}

/**
 * Zoom gate for an anomaly marker. L1/L2 (+ unknown) are hidden at country zoom
 * and tier in at regional+ (REGIONAL_VISIBLE_FAR_M); L3/L4 return `undefined`
 * (no gate → visible at all zoom tiers, the always-on urgent signal).
 */
export function anomalyGate(
  style: HotspotLevelStyle,
): Cesium.DistanceDisplayCondition | undefined {
  return style.hideAtCountry
    ? new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M)
    : undefined;
}

/**
 * The EXACT-ACT disc country-zoom declutter curve (#410, Icon Council Ruling 1): full size near
 * (≤200 km) easing DOWN to a per-level FLOOR at continental range (≥4000 km), mirroring the
 * aircraft `scaleByDistance` precedent (MapView).
 *
 * Founder NIGHT ruling 1 (2026-07-06): the floor is now LEVEL-AWARE (ICON_LADDER.countryFloor) so
 * RL4 barely shrinks (≥0.85×, DOMINANT at national zoom) while lower levels shrink more (L3 ~0.7×,
 * L1/L2/unknown ~0.55×). Because the near value is 1.0 for every level AND a higher level's floor
 * is ≥ every lower level's, RL4's curve sits at or above each lower level's curve at EVERY eye
 * distance; combined with RL4's larger baseSize this makes RL4 ≥ any lower-RL disc at all zooms —
 * the founder's cross-family RL4 size-coherence invariant, now provable per-level rather than by a
 * single shared curve (crossFamilyIconSize.lock.test.ts). The RL anomaly families apply it to
 * their always-on L3/L4 dots via {@link anomalyScale}; OC fires (never hidden at country zoom)
 * apply it unconditionally at their own level so a low-RL fire shrinks below a scaled RL4 marker.
 */
export function discCountryScale(level?: ResponseLevel | 'unknown'): Cesium.NearFarScalar {
  const floor =
    level != null && level !== 'unknown' ? ICON_LADDER[level].countryFloor : ICON_LADDER_UNKNOWN_FLOOR;
  return new Cesium.NearFarScalar(2.0e5, 1.0, 4.0e6, floor);
}

/**
 * Country-zoom declutter for the always-on L3/L4 anomaly dots (#410, Icon Council
 * Ruling 1). The bumped L3/L4 sizes (22/26px) read well up close but would bloat
 * the national view, so the dot SCALES DOWN with eye distance on {@link discCountryScale}.
 * Returns `undefined` for `hideAtCountry` styles (L1/L2, acknowledged, unknown), which
 * already drop at country zoom and so never bloat there — leaving them unscaled keeps
 * their render byte-identical.
 */
export function anomalyScale(style: HotspotLevelStyle): Cesium.NearFarScalar | undefined {
  return style.hideAtCountry ? undefined : discCountryScale(style.level);
}

/** A stable key for a fetched view, tolerant of sub-degree camera jitter so a
 *  genuinely-unchanged view re-keys identically across periodic refreshes (the
 *  precondition for the cleared-ghost to fire instead of false-positiving on pan). */
export function bboxKey(b: { west: number; south: number; east: number; north: number }): string {
  const r = (n: number): string => n.toFixed(2);
  return `${r(b.west)},${r(b.south)},${r(b.east)},${r(b.north)}`;
}

/**
 * Manages the Law 3 cleared-ghost lifecycle for ONE anomaly layer: a private
 * CustomDataSource of grey ghost dots the layer's own render() never clears. Call
 * `reconcile()` once per render with the live markers + the view they were fetched
 * for; a marker that departs ON A STABLE VIEW fades to a grey ghost (#4b5563 α0.4,
 * static, regional+ only) for `ttlMs`, then auto-leaves. A re-appearing id cancels
 * its ghost (the marker is live again — e.g. #129 re-alarm returned it to red).
 */
export class ClearedGhostTracker {
  private readonly ds: Cesium.CustomDataSource;
  private prev = new Map<string, Cesium.Cartesian3>();
  private prevKey: string | null = null;
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    private readonly viewer: Cesium.Viewer,
    name: string,
    private readonly ttlMs: number = GHOST_TTL_MS,
  ) {
    this.ds = new Cesium.CustomDataSource(`${name}-ghosts`);
    void viewer.dataSources.add(this.ds);
  }

  reconcile(
    live: ReadonlyArray<{ id: string; position: Cesium.Cartesian3 }>,
    viewKey: string,
  ): void {
    const liveIds = new Set(live.map((l) => l.id));
    // A marker that came back is live again — cancel any pending ghost for it.
    for (const id of liveIds) this.removeGhost(id);
    for (const id of departedGhostIds(this.prev.keys(), liveIds, this.prevKey, viewKey)) {
      const pos = this.prev.get(id);
      if (pos && !this.timers.has(id)) this.spawnGhost(id, pos);
    }
    this.prev = new Map(live.map((l) => [l.id, l.position]));
    this.prevKey = viewKey;
  }

  setVisible(visible: boolean): void {
    this.ds.show = visible;
  }

  /** Live ghost-entity count — test seam. */
  ghostCount(): number {
    return this.ds.entities.values.length;
  }

  destroy(): void {
    for (const t of this.timers.values()) clearTimeout(t);
    this.timers.clear();
    this.prev.clear();
    this.ds.entities.removeAll();
    if (!this.viewer.isDestroyed()) this.viewer.dataSources.remove(this.ds, true);
  }

  private spawnGhost(id: string, position: Cesium.Cartesian3): void {
    this.ds.entities.add({
      id: `ghost:${id}`,
      position,
      point: {
        pixelSize: 9,
        color: Cesium.Color.fromCssColorString(CLEARED_GHOST).withAlpha(CLEARED_GHOST_ALPHA),
        outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
        outlineWidth: 1,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        // A regional+ courtesy — a cleared marker is never re-asserted at country zoom.
        distanceDisplayCondition: new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M),
      },
    });
    this.timers.set(
      id,
      setTimeout(() => this.removeGhost(id), this.ttlMs),
    );
  }

  private removeGhost(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    const ent = this.ds.entities.getById(`ghost:${id}`);
    if (ent) this.ds.entities.remove(ent);
  }
}
